<script setup>
  import { ref, computed, onMounted, useTemplateRef, watchEffect, watch } from 'vue';
  import { useMapDrawingStore } from '@/stores/MapDrawingStore';
  import ControlButton from '@/components/controls/ControlButton.vue';
  import Popup from "@/components/controls/Popup.vue";
  import RangeSlider from "@/components/controls/RangeSlider.vue";
  import Icon from '@/components/icons/Icon.vue';

  const emit = defineEmits(['control-toolbar:click', 'control-toolbar:dblclick', 'shape-selection:click', 'shape-complete:click', 'shape-delete:click', 'range-slider:drag']);

  const props = defineProps({
    drawingManager: {
      type: Object,
      required: true
    },
    shape: {
      type: Object,
      required: true
    },
    activeShape: {
      type: Boolean,
      required: true
    }
  });

  const toolbarElement = useTemplateRef('toolbar-ref');

  const mapDrawingStore = useMapDrawingStore();

  const colors = [
    'hsla(0, 0%, 100%, 0)', // Transparent
    // Neutral Colors (Hue doesn't apply, keeping them at the start)
    'hsl(0, 0%, 100%)',  // White
    'hsl(0, 0%, 90%)',   // Light Gray
    'hsl(0, 0%, 75%)',   // Gray
    'hsl(0, 0%, 50%)',   // Dark Gray
    'hsl(0, 0%, 25%)',   // Charcoal
    'hsl(0, 0%, 0%)',    // Black

    // Sorted by Hue (including pastel colors)
    'hsl(0, 100%, 50%)',    // Red
    'hsl(0, 100%, 90%)',    // Pastel Pink
    'hsl(30, 100%, 50%)',   // Orange
    'hsl(30, 100%, 85%)',   // Pastel Peach
    'hsl(60, 100%, 50%)',   // Yellow
    'hsl(60, 100%, 85%)',   // Pastel Yellow
    'hsl(90, 100%, 50%)',   // Lime Green
    'hsl(120, 100%, 50%)',  // Green
    'hsl(120, 100%, 85%)',  // Pastel Green
    'hsl(150, 100%, 50%)',  // Spring Green
    'hsl(180, 100%, 50%)',  // Cyan
    'hsl(180, 100%, 85%)',  // Pastel Cyan
    'hsl(210, 100%, 50%)',  // Sky Blue
    'hsl(240, 100%, 50%)',  // Blue
    'hsl(240, 100%, 85%)',  // Pastel Blue
    'hsl(270, 100%, 50%)',  // Indigo
    'hsl(300, 100%, 50%)',  // Violet
    'hsl(330, 100%, 50%)',  // Magenta
  ];  

  const strokeWidths = [
    2, 3, 4, 5, 6
  ];

  const shapeNameModel = ref(null);
  const shapeFillOpacity = ref(props.shape.settings.fillOpacity || 100);  
  const shapeStrokeOpacity = ref(props.shape.settings.strokeOpacity || 100);
  const nameInputSubmitState = ref(null);
  const toolbarBoundingRect = ref(null);

  const showToolbar = computed(() => props.shape.latLons.length > 1);
  const showTotalDistance = computed(() => props.shape.meta?.totalDistance && props.shape.meta.totalDistance.length > 0);

  const toolbarPosition = computed(() => {
    const latLons = [...props.shape.latLons];

    if (props.shape.type === 'circle') {
      const circleRadius = props.drawingManager.getDistanceBetweenLatLons(
        latLons[0],
        latLons[1]
      );

      const newRadiusLatLon = props.drawingManager.getLatLonAtDistance({ latLon: latLons[0], distance: circleRadius });
      latLons[1] = newRadiusLatLon;
    }

    const shapeBounds = new google.maps.LatLngBounds();

    latLons.forEach((point) => {
      shapeBounds.extend(point);
    });

    const shapeBoundsNorthEastLatLon = {
      lat: shapeBounds.getNorthEast().lat(), 
      lng: shapeBounds.getNorthEast().lng()
    };

    const shapeBoundsNorthWestLatLon = {
      lat: shapeBounds.getNorthEast().lat(), 
      lng: shapeBounds.getSouthWest().lng()
    };

    const shapeBoundsSouthEastLatLon = {
      lat: shapeBounds.getSouthWest().lat(), 
      lng: shapeBounds.getNorthEast().lng()
    };

    const shapeBoundsSouthWestLatLon = {
      lat: shapeBounds.getSouthWest().lat(), 
      lng: shapeBounds.getSouthWest().lng()
    };

    const centerLatLon = google.maps.geometry.spherical.interpolate(shapeBoundsNorthWestLatLon, shapeBoundsNorthEastLatLon, 0.5)
    const centerPoint = props.drawingManager.getPointFromLatLon(centerLatLon);
    const northEastPoint = props.drawingManager.getPointFromLatLon(shapeBoundsNorthEastLatLon);
    const northWestPoint = props.drawingManager.getPointFromLatLon(shapeBoundsNorthWestLatLon);
    const southWestPoint = props.drawingManager.getPointFromLatLon(shapeBoundsSouthWestLatLon);

    if (toolbarElement.value) {
      const toolbarBoundingRect = toolbarElement.value.getBoundingClientRect()
      const offsetY = toolbarBoundingRect.height / 2;
      const offsetX = toolbarBoundingRect.width / 2;
  
      centerPoint.x = Math.max(centerPoint.x, Math.min(offsetX, northEastPoint.x + offsetX));
      centerPoint.y = Math.max(centerPoint.y, Math.min(75, southWestPoint.y));
  
      centerPoint.x = Math.min(centerPoint.x, Math.max(window.innerWidth - offsetX, northWestPoint.x - offsetX));
      centerPoint.y = Math.min(centerPoint.y, Math.max(window.innerHeight - offsetY, northEastPoint.y));
    }

    return centerPoint;
  })

  const inputSubmitActive = computed(() => {
    return shapeNameModel.value !== props.shape.name && shapeNameModel.value.length > 0 || nameInputSubmitState.value === 'submitted';
  })

  const handleShapeCompleteClick = (e) => {
    mapDrawingStore.setDrawingMode('viewing');

    emit('shape-complete:click');
  }

  const handleInputChange = (e) => {
    mapDrawingStore.setShapeName(props.shape.id, shapeNameModel.value);

    nameInputSubmitState.value = 'submitted';

    setTimeout(() => {
      nameInputSubmitState.value = null;
    }, 1000);
  }

  const handleShapeDeleteClick = (e) => {
    emit('shape-delete:click');
  }

  const handleToolbarClick = (e) => {
    if (!props.activeShape) {
      emit('shape-selection:click', {
        id: props.shape.id,
      });
    } else {          
      emit('control-toolbar:click', {
        type: 'ignore',
      });
    }
  }

  const handlePopupToggle = (e) => {
    emit('control-toolbar:click', {
      type: 'ignore',
    });
  }

  const handleInputClick = (e) => {
    if (e.target === document.activeElement) {
      return;
    }

    e.target.focus();
  }

  const handleInputDoubleClick = (e) => {
    if (e.target === document.activeElement) {
      e.target.select();
    }
  }

  const handlePointerdown = (e) => {
    emit('range-slider:drag', {
      type: 'ignore',
    });
  }

  const handleInputKeydown = (e) => {
    if (e.key === 'Enter') {
      handleInputChange();
    }
  }

  onMounted(() => {
    shapeNameModel.value = props.shape.name;
  });
</script>

<template>
  <div 
    v-if="showToolbar" 
    ref="toolbar-ref"
    @dragstart.stop.prevent
    @click="handleToolbarClick"
    class="jwx-map-drawing__shape-toolbar" 
    :style="{ left: toolbarPosition.x + 'px', top: toolbarPosition.y + 'px' }"
    :data-active="props.activeShape"
  >
    <div 
    class="jwx-wrapper-block jwx-wrapper-block--transparent-border jwx-wrapper-block--row" 
      data-shape="deep-round"
      data-responsive="false"
      :data-active="props.activeShape" 
      :data-meta="showTotalDistance"
    >
    <div 
      class="jwx-wrapper-block__section"
    >
      <div class="jwx-wrapper-block__content jwx-wrapper-block__items w-100">
        <div class="jwx-map-input-control w-100">
          <input
            @keydown="handleInputKeydown"
            @click="handleInputClick"
            @dblclick="handleInputDoubleClick"
            id="name-input"
            name="name-input"
            type="text"
            class="jwx-map-input-control__text-input"
            :disabled="!props.activeShape"
            v-model="shapeNameModel"
          />
          <button
            v-if="props.activeShape"
            @click.prevent.stop="handleInputChange"
            class="jwx-map-input-control__input-submit"
            :data-submit-state="nameInputSubmitState"
            :id="`name-submit-${props.shape.id}`"
            :disabled="!inputSubmitActive"
          >
            <Icon
              name="arrow-right"
              class="jwx-button-control__icon"
            />
          </button>
        </div>
      </div>
    </div>
    <div v-if="props.activeShape" class="jwx-wrapper-block__section">
      <div class="jwx-wrapper-block__group">
        <ul class="jwx-wrapper-block__content jwx-wrapper-block__items">
          <li class="jwx-wrapper-block__item">
            <Popup
              @popup:toggle="handlePopupToggle"
              id="shape-stroke-color-dropdown"
              positionY="bottom"
              positionX="center"
            >
              <template #button-content>
                <div class="jwx-button-control">
                  <div
                    class="jwx-toolbar__stroke-color-action-icon"
                    :style="{ '--jwx-shape-stoke-color': props.shape.settings.strokeColor }"
                  ></div>
                </div>
              </template>              
              <template #menu-content>
                <div @click="handleToolbarClick" class="jwx-toolbar__dropdown-menu">
                  <div class="jwx-toolbar__dropdown-section">
                    <div class="jwx-toolbar__dropdown-section-header">
                      <h4 class="jwx-toolbar__dropdown-section-title">Stroke Color</h4>
                    </div>                
                    <ul class="jwx-toolbar__swatch-grid">
                      <li v-for="color in colors" :key="color" class="jwx-toolbar__swatch-grid-item">
                        <button
                          @click="mapDrawingStore.setShapeStrokeColor(color)"
                          class="jwx-toolbar__swatch"
                        >
                          <div
                            class="jwx-toolbar__color-swatch"
                            :style="{ '--jwx-swatch-color': color }"
                            :data-selected="props.shape.settings.strokeColor === color"
                          ></div>
                        </button>
                      </li>
                    </ul>
                  </div>
                  <div class="jwx-toolbar__dropdown-section">
                    <div class="jwx-toolbar__dropdown-section-header">
                      <h4 class="jwx-toolbar__dropdown-section-title">Stroke Opacity</h4>
                      <p class="jwx-toolbar__dropdown-section-meta">
                        {{ shapeStrokeOpacity }}%
                      </p>
                    </div>
                    <div class="jwx-toolbar__dropdown-section-content">
                      <RangeSlider
                        @change="mapDrawingStore.setShapeStrokeOpacity(shapeStrokeOpacity, props.shape.id)"
                        @pointerdown="handlePointerdown"
                        id="shape-stroke-opacity"
                        class="jwx-map-range-control__slider"
                        :min="0"
                        :max="100"
                        v-model.number="shapeStrokeOpacity"
                      />
                    </div>
                  </div>                  
                </div>
              </template>
            </Popup>
          </li>
          <li v-if="props.shape.type !== 'polyline'" class="jwx-wrapper-block__item">
            <Popup
              @popup:toggle="handlePopupToggle"
              id="shape-fill-color-dropdown"
              class="jwx-toolbar__dropdown"
              positionY="bottom"
              positionX="center"
            >
              <template #button-content>
                <div class="jwx-button-control">
                  <div
                    class="jwx-toolbar__fill-color-action-icon"
                    :style="{ '--jwx-shape-fill-color': props.shape.settings.fill }"
                  ></div>
                </div>
              </template>              
              <template #menu-content>
                <div @click="handleToolbarClick" class="jwx-toolbar__dropdown-menu">
                  <div class="jwx-toolbar__dropdown-section">
                    <div class="jwx-toolbar__dropdown-section-header">
                      <h4 class="jwx-toolbar__dropdown-section-title">Fill Color</h4>
                    </div>
                    <ul class="jwx-toolbar__swatch-grid">
                      <li v-for="color in colors" :key="color" class="jwx-toolbar__swatch-grid-item">
                        <button
                          @click="mapDrawingStore.setShapeFillColor(color)"
                          class="jwx-toolbar__swatch"
                        >
                          <div
                            class="jwx-toolbar__color-swatch"
                            :style="{ '--jwx-swatch-color': color }"
                            :data-selected="props.shape.settings.fill === color"
                          ></div>
                        </button>
                      </li>
                    </ul>
                  </div>
                  <div class="jwx-toolbar__dropdown-section">
                    <div class="jwx-toolbar__dropdown-section-header">
                      <h4 class="jwx-toolbar__dropdown-section-title">Fill Opacity</h4>
                      <p class="jwx-toolbar__dropdown-section-meta">
                        {{ shapeFillOpacity }}%
                      </p>
                    </div>
                    <div class="jwx-toolbar__dropdown-section-content">
                      <RangeSlider
                        @change="mapDrawingStore.setShapeFillOpacity(shapeFillOpacity, props.shape.id)"
                        @pointerdown="handlePointerdown"
                        id="shape-fill-opacity"
                        class="jwx-map-range-control__slider"
                        :min="0"
                        :max="100"
                        v-model.number="shapeFillOpacity"
                      />
                    </div>
                  </div>
                </div>
              </template>
            </Popup>
          </li>
          <li class="jwx-wrapper-block__item">
            <Popup
              @popup:toggle="handlePopupToggle"
              id="shape-stroke-width-selector"
              positionY="bottom"
              positionX="center"
            >
              <template #button-content>
                <div class="jwx-button-control">
                  <div class="jwx-toolbar__stroke-width-action-icon" >
                    <svg
                      :style="{ '--jwx-shape-stroke-width': props.shape.settings.strokeWidth }"
                    >
                      <line x1="10%" y1="50%" x2="90%" y2="50%" stroke-linecap="round" />
                    </svg>
                  </div>
                </div>
              </template>              
              <template #menu-content>
                <div @click="handleToolbarClick" class="jwx-toolbar__dropdown-menu">
                  <div class="jwx-toolbar__dropdown-section">
                    <ul class="jwx-toolbar__swatch-column">
                      <li v-for="strokeWidth in strokeWidths" :key="strokeWidth" class="jwx-toolbar__swatch-grid-item">
                        <button
                          @click="mapDrawingStore.setShapeStrokeWidth(strokeWidth)"
                          class="jwx-toolbar__swatch"
                        >
                          <div class="jwx-toolbar__stroke-width-swatch">
                            <svg
                              class="jwx-toolbar__stroke-width-swatch"
                              :style="{ '--jwx-swatch-stroke-width': strokeWidth }"
                               :data-selected="props.shape.settings.strokeWidth === strokeWidth"
                            >
                              <line x1="10%" y1="50%" x2="90%" y2="50%" stroke-linecap="round" />
                            </svg>
                          </div>
                        </button>
                      </li>
                    </ul>  
                  </div>     
                </div>
              </template>
            </Popup>
          </li>
        </ul>
      </div>
      <div class="jwx-wrapper-block__group">
        <ul class="jwx-wrapper-block__content jwx-wrapper-block__items">
          <li class="jwx-wrapper-block__item">
            <ControlButton
              @click.stop="handleShapeCompleteClick"
              data-type="success"
            >
              <Icon 
                name="checkmark" 
                class="jwx-button-control__icon"
                data-contstrain="width"
              />  
            </ControlButton>
          </li>
        </ul>
        <ul class="jwx-wrapper-block__content jwx-wrapper-block__items">
          <li class="jwx-wrapper-block__item">
            <ControlButton
              @click.stop="handleShapeDeleteClick"
              data-type="danger"
            >
              <Icon 
                name="trash" 
                class="jwx-button-control__icon"
              />  
            </ControlButton>
          </li>
        </ul>
      </div>
    </div>
    <div v-if="showTotalDistance" class="jwx-wrapper-block__section">
      <div class="jwx-wrapper-block__content w-100">
        <div class="jwx-wrapper-block__meta">
          <p class="jwx-wrapper-block__meta-key">Distance</p>
          <p class="jwx-wrapper-block__meta-value">{{ props.shape.meta.totalDistance }}</p>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>